import React, { useEffect, useState } from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  Switch, Pagination, Typography, CircularProgress,
  Paper, Box, Stack, useTheme, useMediaQuery
} from '@mui/material';
import { BACKEND_BASE_URL } from '../config';
import api from '../components/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const PAGE_SIZE = 150;

const ReportedIssuesPage = () => {
  const [issues, setIssues] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedIssueIds, setExpandedIssueIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');


  const token = localStorage.getItem('access_token');
  const user = JSON.parse(localStorage.getItem('user'));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchIssues = async (pageNum = 1, query = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(
        `${BACKEND_BASE_URL}/api/reported-issues/?page=${pageNum}&page_size=${PAGE_SIZE}&search=${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIssues(res.data.results);
      setCount(Math.ceil(res.data.count / PAGE_SIZE));
    } catch (err) {
      console.error('Error fetching issues:', err);
      setError('Failed to load reported issues. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const handleSearch = () => {
    setPage(1);
    fetchIssues(1, searchQuery);
  };


  useEffect(() => {
    fetchIssues(page);
  }, [page]);

  const toggleResolved = async (id, current) => {
    setUpdatingId(id);
    try {
      await api.patch(
        `${BACKEND_BASE_URL}/api/reported-issues/${id}/`,
        {
          resolved: !current,
          resolved_by: !current ? user?.id : null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(`Issue marked as ${!current ? 'Resolved' : 'Unresolved'}! 🎯`);
      fetchIssues(page);
    } catch (err) {
      console.error('Error updating resolved status:', err);
      toast.error('Failed to update resolved status. ⚠️');
    }
    finally {
      setUpdatingId(null);
    }
  };

  const toggleIssueExpansion = (id) => {
    setExpandedIssueIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };




  return (
    <Box p={isMobile ? 2 : 4}>
      <Stack direction="column" spacing={2}>


        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight={600}>
            Reported Issues
          </Typography>
          <Box display="flex" gap={1}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Search"
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontSize: '14px',
                width: '250px'
              }}
            />

            <button
              onClick={handleSearch}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#1976d2',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Search
            </button>
          </Box>
        </Box>


        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow >
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>S.No.</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>User</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Town</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Video Title</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Summary Title</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Issue</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Reported At</b></TableCell> {/* ⬅️ New column */}
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Resolved</b></TableCell>
                    <TableCell align="center" sx={{ color: 'white', backgroundColor: '#152F8F' }}><b>Resolved By</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issues.map((issue, index) => (
                    <TableRow
                      key={issue.id}
                      hover
                      sx={{
                        transition: 'background 0.2s ease',
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                      <TableCell >{issue.user_email}</TableCell>
                      <TableCell>{issue.town_name_readable}</TableCell>
                      <TableCell >{issue.video_title || '—'}</TableCell>
                      <TableCell >{issue.summary_name || '—'}</TableCell>
                      <TableCell>
                        {issue.issue.length > 100 ? (
                          <>
                            {expandedIssueIds.includes(issue.id)
                              ? issue.issue
                              : `${issue.issue.slice(0, 100)}...`}{' '}
                            <Typography
                              component="span"
                              variant="body2"
                              color="primary"
                              onClick={() => toggleIssueExpansion(issue.id)}
                              sx={{ cursor: 'pointer', fontWeight: 500 }}
                            >
                              {expandedIssueIds.includes(issue.id) ? 'Read less' : 'Read more'}
                            </Typography>
                          </>
                        ) : (
                          issue.issue
                        )}
                      </TableCell>

                      <TableCell>
                        {new Date(issue.created_at).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })}
                      </TableCell>


                      <TableCell>
                        <Switch
                          checked={issue.resolved}
                          onChange={() => toggleResolved(issue.id, issue.resolved)}
                          color="primary"
                          disabled={updatingId === issue.id}
                        />
                      </TableCell>
                      <TableCell>{issue.resolved_by || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>

              </Table>
            </Box>
          )}
        </Paper>

        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={count}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      </Stack>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
        toastStyle={{
          backgroundColor: '#f9f9f9',
          color: '#333',
          fontSize: '14px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
        }}
        bodyStyle={{
          padding: '10px 12px',
        }}
      />


    </Box>
  );
};

export default ReportedIssuesPage;
